import { useCallback, useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: HTMLElement, params: Record<string, unknown>) => number
      reset: (id: number) => void
    }
  }
}

export function useRecaptcha() {
  const [token, setToken] = useState<string | null>(null)
  const widgetIdRef = useRef<number | null>(null)
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined
  const enabled = !!siteKey

  useEffect(() => {
    if (!enabled) return
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
    script.async = true
    document.head.appendChild(script)
  }, [enabled])

  const widgetRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !enabled) return
      if (widgetIdRef.current !== null) return

      const tryRender = () => {
        const grc = window.grecaptcha
        if (!grc?.render) {
          setTimeout(tryRender, 500)
          return
        }

        try {
          widgetIdRef.current = grc.render(node, {
            sitekey: siteKey,
            callback: (t: string) => setToken(t),
            'expired-callback': () => setToken(null),
            'error-callback': () => setToken(null),
          })
        } catch (e) {
          console.error('reCAPTCHA render error:', e)
        }
      }

      tryRender()
    },
    [enabled, siteKey]
  )

  const reset = useCallback(() => {
    setToken(null)
    const grc = window.grecaptcha
    if (grc && widgetIdRef.current !== null) {
      grc.reset(widgetIdRef.current)
      widgetIdRef.current = null
    }
  }, [])

  return { token, widgetRef, reset, enabled }
}
